import { useEffect, useReducer, useState } from 'react';
import { Header } from './Header';
import { VideoArea } from './VideoArea';
import { ChatPanel, type Message } from './ChatPanel';
import { MobileDrawer } from './MobileDrawer';
import { Controls } from './Controls';
import { NetworkErrorOverlay } from '@/components/overlays/NetworkErrorOverlay.tsx';
import { useAuth } from '@/contexts/AuthContext';
import { useMediaDevices } from '@/contexts/MediaDevicesContext';
import { useDataChannelMessage } from '@/hooks/useDataChannelMessage';
import { RecordingProtectionProvider } from '@/contexts/RecordingProtectionContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Signals, useSignaling } from '@/contexts/SignalingContext.tsx';
import { usePeer } from '@/contexts/WebRTCContext.tsx';
import config from '@/config';
import { ErrorMessageOverlay } from '@/components/overlays/ErrorMessageOverlay.tsx';
import { AppError, ErrorCode } from '@/types/errors.ts';

import { ReportData } from './types';
import { reducer, initialState } from './reducer';
import { REPORT_REASONS, CONNECTION_STATUS } from './constants';
import { captureScreenshot } from './utils';
import { useVideoChatSignalHandlers } from './signalHandlers';
import { handleRetryPermissions } from './callbacks/retryHandlers.ts';
import { useAuthenticatedFetch } from '@/hooks/useAuthenticatedFetch';

export function VideoChat() {
  /** Global Triggers
   *  - anyone can set this global state and all the components will act locally based on the state of the event
   */
  const [ triggerResetConnection, setTriggerResetConnection ] = useState(false);
  const [ reportModalOpen, setReportModalOpen ] = useState(false);
  const [ reportReason, setReportReason ] = useState('');
  const [ reportDescription, setReportDescription ] = useState('');
  const [ currentReport, setCurrentReport ] = useState<Omit<ReportData, 'reason' | 'description'> | null>(null);
  const [ error, setError ] = useState<AppError | null>(null);

  const { user } = useAuth();

  const {
    localStream,
    isVideoEnabled,
    isAudioEnabled,
    error: mediaError,
    requestMediaAccess,
    hasPermissions
  } = useMediaDevices();

  const {
    sendSignal,
    isConnected,
    reconnect,
    reconnectionState
  } = useSignaling();

  const {
    createDataChannel,
    rtcConnectionState,
    error: rtcConnectionError,
    handleRetryRtcConnection,
    setIceConfiguration,
    addLocalTracks,
    generalDataChannel,
    resetTrigger: resetPeer,
    isPeerAudioMuted,
    isPeerVideoHidden,
    togglePeerAudioMute,
    togglePeerVideoHide
  } = usePeer();

  const [ state, dispatch ] = useReducer(reducer, initialState);

  // Derived connection status - declarative mapping
  const connectionDisplayStatus = isConnected
    ? CONNECTION_STATUS.online
    : reconnectionState
      ? CONNECTION_STATUS.disconnected
      : CONNECTION_STATUS.connecting;

  const { chat } = state;
  const { chatChannel } = chat;

  useVideoChatSignalHandlers({
    localStream,
    error,
    setError,
    addLocalTracks,
    dispatch,
    setIceConfiguration
  });

  const handleToggleChat = (open?: boolean) => {
    if (open) { dispatch({ type: 'TOGGLE_CHAT', payload: open }); } else { dispatch({ type: 'TOGGLE_CHAT' }); }
  };

  const handleSkipUser = () => {
    if (!isConnected || rtcConnectionState !== 'connected') return;

    console.log('Skip to next user');
    sendSignal({
      signal: Signals.SkipUser
    });
  };

  const handleAddMessage = (message: Message) => {
    dispatch({ type: 'ADD_MESSAGE', payload: message });
  };

  const handleReportClick = () => {
    console.log('Report issue');

    const selfVideoElement = document.querySelector<HTMLVideoElement>('#selfVideo');
    const peerVideoElement = document.querySelector<HTMLVideoElement>('#remoteVideo');

    const selfScreenshot = captureScreenshot(selfVideoElement);
    const peerScreenshot = captureScreenshot(peerVideoElement);

    const reportData: Omit<ReportData, 'reason' | 'description'> = {
      timestamp: new Date().toISOString(),
      selfScreenshot,
      peerScreenshot,
      chatLogs: [ ...state.chat.messages ],
      peerId: state.peer.uid,
      selfId: state.self.uid
    };

    setCurrentReport(reportData);
    setReportModalOpen(true);
  };

  const handleReportSubmit = () => {
    if (!currentReport) return;

    console.debug('Report submitted', { reportReason, reportDescription });

    const completeReport: ReportData = {
      ...currentReport,
      reason: reportReason,
      description: reportDescription
    };

    dispatch({ type: 'ADD_REPORT', payload: completeReport });

    console.log('Reports queue:', [ ...state.reports, completeReport ]);

    setReportModalOpen(false);
    setReportReason('');
    setReportDescription('');
    setCurrentReport(null);
  };

  const handleReportCancel = () => {
    console.debug('Report cancelled');
    setReportModalOpen(false);
    setReportReason('');
    setReportDescription('');
    setCurrentReport(null);
  };

  // set errors
  useEffect(() => {
    if (error) return;
    if (rtcConnectionError) {
      setError(rtcConnectionError);
      return;
    }
    if (mediaError && !hasPermissions) {
      setError({ code: ErrorCode.MediaPermissionDenied });
      return;
    }
  }, [ error, hasPermissions, mediaError, rtcConnectionError ]);

  useEffect(() => {
    if (resetPeer) {
      console.log('Resetting peer event');
      dispatch({ type: 'RESET' });
    }
  }, [ resetPeer ]);

  useEffect(() => {
    if (localStream && isConnected) {
      sendSignal({ signal: Signals.UserMediaSet });
    }
  }, [ isConnected, localStream, sendSignal ]);

  useDataChannelMessage(generalDataChannel, 'name-exchange', user?.name);
  useDataChannelMessage(generalDataChannel, 'video-toggle', isVideoEnabled ? 'on' : 'off');
  useDataChannelMessage(generalDataChannel, 'audio-toggle', isAudioEnabled ? 'on' : 'off');

  useEffect(() => {
    if (chatChannel) return;

    createDataChannel('text-chat', {
      onMessage: (event) => {
        const formattedTime = new Date().toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        handleAddMessage({
          id: Date.now().toString(),
          sender: 'peer',
          text: event.data,
          time: formattedTime
        });
      },
      onOpen: (dataChannel) => {
        dispatch({ type: 'SET_CHAT_CHANNEL', payload: dataChannel });
      },
      onClose: () => {
        console.log('Chat channel closed');
      }
    }, { negotiated: true, id: 110 });

  }, [ chatChannel ]);

  // Automatically stop session when WebSocket disconnects during active session
  useEffect(() => {
    if (!isConnected && state.sessionActive) {
      console.log('WebSocket disconnected during active session - stopping session');
      dispatch({ type: 'SET_SESSION_ACTIVE', payload: false });
    }
  }, [ isConnected, state.sessionActive ]);

  const { reports } = state;

  // Automatically submit reports when they're added - declarative API sync
  useAuthenticatedFetch(
    config.endpoints.reporting,
    'POST',
    reports,
    {
      skip: reports.length === 0,
      onSuccess: () => dispatch({ type: 'FLUSH_REPORTS' })
    }
  );

  return (
    <RecordingProtectionProvider>
    <div className="flex flex-col h-screen">
      <Header
        connectionStatus={connectionDisplayStatus}
        sessionActive={state.sessionActive}
        isPeerAudioMuted={isPeerAudioMuted}
        isPeerVideoHidden={isPeerVideoHidden}
        onTogglePeerAudio={togglePeerAudioMute}
        onTogglePeerVideo={togglePeerVideoHide}
        onShowVideoFilters={() => {
          // TODO: Implement video filters modal/popover
          console.log('Show video filters');
        }}
        onChangeLayout={() => dispatch({ type: 'CHANGE_LAYOUT' })}
        onReport={handleReportClick}
        rtcConnectionState={rtcConnectionState}
      />
      {/* Show network error overlay when WebSocket disconnects during active session */}
      <NetworkErrorOverlay
        isConnected={isConnected}
        reconnectionState={reconnectionState}
        onRetry={reconnect}
        sessionActive={state.sessionActive}
      />
      <ErrorMessageOverlay
        error={error}
        retryCallbacks={{
          [ErrorCode.NoLocalStream]: handleRetryPermissions({ requestMediaAccess, setError }),
          [ErrorCode.MediaPermissionDenied]: handleRetryPermissions({ requestMediaAccess, setError }),
          [ErrorCode.RTCConnectionFailed]: handleRetryRtcConnection
        }}
      />
      <main className="flex-1 flex flex-col md:flex-row overflow-hidden">
        <div className={`flex flex-col ${state.chat.chatOpen ? 'flex-1' : 'w-full'}`}>
          <VideoArea
            videoSizeRatio={state.self.video.videoSizeRatio}
            onSliderChange={(value) => dispatch({ type: 'SET_VIDEO_SIZE_RATIO', payload: value })}
            openDrawer={() => dispatch({ type: 'SET_DRAWER_OPEN', payload: true })}
            currentLayout={state.video.currentLayout}
            swapPositions={state.video.swapPositions}
            onReport={handleReportClick}
            onSwapPositions={() => dispatch({ type: 'SWAP_POSITIONS' })}
            triggerResetConnection={triggerResetConnection}
            setTriggerResetConnection={setTriggerResetConnection}
            sessionActive={state.sessionActive}
            handleSessionActive={(sessionActive) => dispatch({
              type: 'SET_SESSION_ACTIVE',
              payload: sessionActive
            })}
            generalDataChannel={generalDataChannel}
          />

          <Controls
            onToggleChat={handleToggleChat}
            peerConnectionState={rtcConnectionState}
            onSkipUser={handleSkipUser}
            sessionActive={state.sessionActive}
            handleSessionActive={(sessionActive) => dispatch({
              type: 'SET_SESSION_ACTIVE',
              payload: sessionActive
            })}
          />
        </div>
        {/* Only render ChatPanel if chat is open */}
        {state.chat.chatOpen && (
          <ChatPanel
            messages={state.chat.messages}
            isChatOpen={state.chat.chatOpen}
            autoTranslate={state.chat.autoTranslate}
            onToggleAutoTranslate={() => dispatch({ type: 'TOGGLE_AUTO_TRANSLATE' })}
            onToggleTranslation={(messageId) => dispatch({
              type: 'SHOW_TRANSLATION',
              payload: { messageId }
            })}
            onUpdateTranslation={(messageId, translation) => dispatch({
              type: 'UPDATE_MESSAGE_TRANSLATION',
              payload: { messageId, translation }
            })}
            handleAddMessage={handleAddMessage}
            handleToggleChat={handleToggleChat}
            chatChannel={chatChannel}
            peerConnectionState={rtcConnectionState}
            triggerResetConnection={triggerResetConnection}
            setTriggerResetConnection={setTriggerResetConnection}
          />
        )}
      </main>

      <MobileDrawer
        isOpen={state.video.drawerOpen}
        onClose={() => dispatch({ type: 'SET_DRAWER_OPEN', payload: false })}
      />
      {/* Report Modal */}
      <Dialog open={reportModalOpen} onOpenChange={(open) => !open && handleReportCancel()}>
        <DialogContent className="bg-[#1E293B] border border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Report User</DialogTitle>
            <DialogDescription className="text-gray-400">
              Please provide details about the issue you&#39;re experiencing with this user.
              {currentReport && <span className="block mt-1 text-xs text-green-500">
                                Screenshots have been captured and chat logs have been saved
              </span>}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="report-reason">Reason</Label>
              <Select value={reportReason} onValueChange={setReportReason}>
                <SelectTrigger className="bg-[#131A26] border-slate-700">
                  <SelectValue placeholder="Select a reason"/>
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border border-slate-700 text-white">
                  {REPORT_REASONS.map(reason => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="report-description">Description</Label>
              <Textarea
                id="report-description"
                placeholder="Please provide more details..."
                className="h-32 bg-[#131A26] border-slate-700 text-white placeholder:text-gray-500 resize-none"
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row sm:justify-end gap-2">
            <Button
              variant="outline"
              onClick={handleReportCancel}
              className="bg-transparent hover:bg-[#273449] text-white border border-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReportSubmit}
              className="bg-red-800 hover:bg-red-700 text-white"
              disabled={!reportReason}
            >
              Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </RecordingProtectionProvider>
  );
}
